import { Injectable } from '@angular/core';
import { map, tap, switchMap, filter } from 'rxjs/operators';
import {
	ChangePassword,
	User,
	UserToken,
	AuthResponse,
	UserTokenClaims,
	UserClaimTypeExtended,
	ResetPassword,
	Register,
	UserDeleteResult,
	EnableOrDisableResult,
	MigrateUserToNewDashResult,
} from 'models';
import { Observable, BehaviorSubject, empty } from 'rxjs';
import { Router, NavigationError, NavigationEnd, NavigationExtras } from '@angular/router';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import * as jwtDecode from 'jwt-decode';

const localStorageUserKey = 'gh:user';
const localStorageControlledUserKey = 'gh:controlledUser';
const sessionStorageUserId = 'gh-admin:user-id';
const sessionStorageUserSearchString = 'gh-admin:user-search-string';

/**
 * userSections order is important for users with multiple roles.
 */
const userSections = <{ [key in UserClaimTypeExtended]: string }>{
	Admin: '/admin',
	Platform: '/platform',
	Host: '/host',
	KnowYourGuest: '/kyg',
	Guest: '/guest',
	NonMember: '/guest',
	Unauthenticated: '/auth',
};

@Injectable({
	providedIn: 'root',
})
export class UserService {
	constructor(private http: HttpClient, private router: Router) {
		// Identifying Users Dashboard
		this.userToken$.subscribe(_ => this.navigateToHomepage());

		// IMPORTANT fallback when user enters unexisted path in browser
		// it navigates to homepage. No effect when user clicks corrupted routerLink
		//
		// @ToDo create Global Error Handler otherwise error shown after redirection on console
		// ERROR Error: Uncaught (in promise): HttpErrorResponse
		// https://blog.angularindepth.com/expecting-the-unexpected-best-practices-for-error-handling-in-angular-21c3662ef9e4
		this.router.events.subscribe(event => {
			if (event instanceof NavigationError && !this.router.navigated) {
				this.navigateToHomepage(true);
			}
		});
	}

	private userSubject: BehaviorSubject<User | null> = new BehaviorSubject<null>(null);

	public get user$(): Observable<User | null> {
		return this.getUser();
	}

	/**
	 * UserToken holds only user authentication data
	 */
	private userTokenSubject: BehaviorSubject<UserToken | null> = new BehaviorSubject<UserToken | null>(this.getUserToken());

	public get userToken$(): Observable<UserToken | null> {
		return this.userTokenSubject.asObservable();
	}

	public get userToken(): UserToken | null {
		return this.userTokenSubject.value;
	}

	set saveUserSearchString(value: string) {
		sessionStorage.setItem(sessionStorageUserSearchString, value);
	}

	get getUserSearchString(): string {
		return sessionStorage.getItem(sessionStorageUserSearchString);
	}

	/**
	 * Retrieving userToken from localStorage
	 */
	public getUserToken(): UserToken | null {
		const token = localStorage.getItem(localStorageControlledUserKey) || localStorage.getItem(localStorageUserKey);

		if (!token) return null;

		const userTokenTokenClaims = this.decodeUserToken(token);
		if (userTokenTokenClaims) {
			return {
				token: token,
				claims: userTokenTokenClaims,
				switchedFromAdmin: !!localStorage.getItem(localStorageControlledUserKey),
			};
		} else return null;
	}

	/**
	 * Gets new access token from server, updates token in local storage,
	 * updates user$ and updates UserTokenSubject
	 *
	 * This must be executed when Role or Claims changes
	 *
	 * @return Observable<User>
	 */
	refreshUserToken(): Observable<User> {
		return this.http.get<AuthResponse>('api/accounts/refreshtoken').pipe(
			switchMap(authResponse => {
				if (authResponse && authResponse.access_token) {
					const userTokenClaims = this.decodeUserToken(authResponse.access_token);
					if (userTokenClaims) {
						const userToken = {
							token: authResponse.access_token,
							claims: userTokenClaims,
						};
						this.setUserToken(userToken, true);
						return this.getUser();
					} else return empty();
				}
			})
		);
	}

	/**
	 * Saving UserToken token to localStorage and setting userTokenSubject
	 */
	public setUserToken(userToken: UserToken, updateLocalStorage: boolean = true, isAdministerUser: boolean = false): void {
		if (updateLocalStorage && userToken && userToken.token) localStorage.setItem(isAdministerUser ? localStorageControlledUserKey : localStorageUserKey, userToken.token);
		this.userTokenSubject.next(userToken);
	}

	/**
	 * Saving UserToken token to localStorage passed from verification tool
	 */
	public setUserTokenPassThrough(token: string): void {
		let userToken = new UserToken();
		userToken.token = token;
		userToken.switchedFromAdmin = false;

		this.setUserToken(userToken, true);
	}

	private decodeUserToken(token: string): UserTokenClaims | null {
		if (!token) return null;

		try {
			return jwtDecode<UserTokenClaims>(token);
		} catch (error) {
			this.purgeUserData(true);
			return null;
		}
	}

	public get userTokenClaims(): UserClaimTypeExtended[] {
		const userToken = this.userTokenSubject.value;

		if (!userToken || !userToken.claims) {
			return [];
		}
		if (typeof userToken.claims.extended_claims === 'string') {
			return [userToken.claims.extended_claims];
		}
		if (Array.isArray(userToken.claims.extended_claims)) {
			return userToken.claims.extended_claims;
		}
		return [];
	}

	public get userTokenClaims$(): Observable<UserClaimTypeExtended[]> {
		return this.userToken$.pipe(
			map(userToken => {
				if (!userToken || !userToken.claims) return [];
				else {
					if (typeof userToken.claims.extended_claims === 'string') return [userToken.claims.extended_claims];
					else if (Array.isArray(userToken.claims.extended_claims)) return userToken.claims.extended_claims;
					else [];
				}
			})
		);
	}

	/**
	 * Purging ALL user data userToken data from localStorage, userTokenSubject, userSubject
	 *
	 * If fullReload is true than clears entire localStorage and forceReload window.location
	 */
	private purgeUserData(fullReload: boolean = false): void {
		if (this.userTokenSubject !== null) this.userTokenSubject.next(null);
		if (this.userSubject !== null) this.userSubject.next(null);

		if (!fullReload) {
			localStorage.removeItem(localStorageUserKey);
			localStorage.removeItem(localStorageControlledUserKey);
		} else {
			localStorage.clear();
			window.location.reload(true);
		}
	}

	public navigateToHomepage(forceHomepageLanding: boolean = false, replaceUrl: boolean = false): void {
		let userClaims: UserClaimTypeExtended[] = ['Unauthenticated'];

		const redirect = localStorage.getItem('sh:redirect-host-onboard');
		if (redirect) {
			this.router.navigate(['/guest/dashboard', { outlets: { section: ['host-onboarding'] } }]);
			localStorage.removeItem('sh:redirect');
			return;
		}

		if (this.userTokenClaims.length > 0) {
			userClaims = this.userTokenClaims;
		}

		// forceHomepageLanding if user is outside of all sections he has an access to
		if (userClaims.map(role => this.isWithinUserSection(role)).indexOf(true) === -1) {
			forceHomepageLanding = true;
		}

		// Finding landingRole. Based on the order of userSections
		let landingClaim: UserClaimTypeExtended;
		for (let sectionRole of Object.keys(userSections) as UserClaimTypeExtended[]) {
			if (userClaims.indexOf(sectionRole) !== -1) {
				landingClaim = sectionRole;
				break;
			}
		}

		if (forceHomepageLanding) {
			const userSectionByClaim = this.getUserSection(landingClaim);

			if (userSectionByClaim === '/admin') {
				// We always want to navigate back to the user account overview screen after we logout as an admin.
				const section = ['accounts'];

				if (sessionStorage.getItem(sessionStorageUserId)) section.push(sessionStorage.getItem(sessionStorageUserId));

				const navigationExtras: NavigationExtras = { replaceUrl: replaceUrl };
				const searchString = this.getUserSearchString;
				if (searchString) navigationExtras.queryParams = { searchString };

				this.router.navigate([`${this.getUserSection(landingClaim)}/dashboard`, { outlets: { section } }], navigationExtras);
			} else {
				this.router.navigate([this.getUserSection(landingClaim)], {
					replaceUrl: replaceUrl,
				});
			}

			return;
		}
	}

	/**
	 * Used to get UserSection for current path
	 *
	 * @return Observable<UserRoleExtended>
	 */
	public get userSection$(): Observable<UserClaimTypeExtended | null> {
		return this.router.events.pipe(
			filter(event => event instanceof NavigationEnd),
			map(() => {
				const claim = Object.keys(userSections).find(key => new RegExp('^' + userSections[key]).test(window.location.pathname));
				return (claim as UserClaimTypeExtended) || null;
			})
		);
	}

	public getUserSection(userClaim: UserClaimTypeExtended): string {
		return userSections[userClaim];
	}

	public isWithinUserSection(userClaim: UserClaimTypeExtended): boolean {
		//Better  way to do this ie crete exclusion list
		if (window.location.pathname.includes('/app/') || window.location.pathname.includes('/widgets/')) {
			return true;
		}

		return new RegExp('^' + this.getUserSection(userClaim)).test(window.location.pathname);
	}
	public userHasClaim(claimType: UserClaimTypeExtended): boolean {
		if (Array.isArray(this.userToken.claims.extended_claims) && this.userToken.claims.extended_claims.indexOf(claimType) > -1) {
			return true;
		}

		if (this.userToken.claims.extended_claims === claimType) {
			return true;
		}

		return false;
	}

	public getUser(): Observable<User> {
		return this.http.get<User>('api/users').pipe(tap(user => this.userSubject.next(user)));
	}

	public deleteUser(userId: string): Observable<UserDeleteResult> {
		return this.http.delete<UserDeleteResult>(`api/users/deleteuser/${userId}`);
	}

	public migrateUserToNewDash(userId: string): Observable<MigrateUserToNewDashResult> {
		return this.http.delete<MigrateUserToNewDashResult>(`api/users/MigrateUserToNewDash/${userId}`);
	}

	public getUserById(id: string): Observable<User> {
		return this.http.get<User>(`api/accounts/users/${id}`);
	}

	public createPlatformUser(user: User): Observable<User> {
		return this.http.post<User>('api/accounts/createplatform', user);
	}

	public createPropertyVerificationManager(user: User): Observable<User> {
		return this.http.post<User>('api/accounts/createpropertyverificationmanager', user);
	}

	public createAdminUser(user: User): Observable<User> {
		return this.http.post<User>('api/accounts/createadmin', user);
	}

	public createEDepositUser(user: User): Observable<User> {
		return this.http.post<User>('api/accounts/createEDeposit', user);
	}

	public editUser(user: User): Observable<User> {
		return this.http.put<User>('api/accounts/edit', user).pipe(tap(user => this.userSubject.next(user)));
	}

	public enableStays(userId: string): Observable<EnableOrDisableResult[]> {
		return this.http.put<EnableOrDisableResult[]>(`api/accounts/enablestay/${userId}`, null);
	}

	public disableStays(userId: string): Observable<EnableOrDisableResult[]> {
		return this.http.put<EnableOrDisableResult[]>(`api/accounts/disablestay/${userId}`, null);
	}

	public isStayImportingEnabled(id: string): Observable<boolean> {
		return this.http.get<boolean>(`api/accounts/checkisStayImportingEnabled/${id}`);
	}

	public enableIntegrationLogging(userId: string): Observable<EnableOrDisableResult[]> {
		return this.http.put<EnableOrDisableResult[]>(`api/accounts/enableIntegrationLogging/${userId}`, null);
	}

	public disableIntegrationLogging(userId: string): Observable<EnableOrDisableResult[]> {
		return this.http.put<EnableOrDisableResult[]>(`api/accounts/disableIntegrationLogging/${userId}`, null);
	}

	public isIntegrationLoggingActive(id: string): Observable<boolean> {
		return this.http.get<boolean>(`api/accounts/checkIsIntegrationLoggingActive/${id}`);
	}

	public confirmUserEmail(code: string, id: string): Observable<User> {
		return this.http
			.get<void>('api/accounts/confirmemail', {
				params: new HttpParams({
					fromObject: {
						code: code,
						userid: id,
					},
				}),
			})
			.pipe(switchMap(_ => this.refreshUserToken()));
	}

	public confirmRegisterSuperhogEmail(code: string, id: string): Observable<Register> {
		return this.http.get<Register>('api/accounts/confirmregistersuperhogemail', {
			params: new HttpParams({
				fromObject: {
					code: code,
					userid: id,
				},
			}),
		});
	}

	public completeSuperhogUserWww(register: Register): Observable<User> {
		return this.http.post<User>('api/accounts/completesuperhoguserwww', register);
	}

	public forgotUserPassword(email: string): Observable<void> {
		return this.http.post<void>('api/accounts/forgotpassword', email);
	}

	public resetUserPassword(resetPassword: ResetPassword): Observable<void> {
		return this.http.post<void>('api/accounts/resetpassword', resetPassword).pipe(tap(_ => this.purgeUserData()));
	}

	public changePassword(changePassword: ChangePassword): Observable<void> {
		return this.http.post<void>('api/accounts/changepassword', changePassword).pipe(tap(_ => this.purgeUserData()));
	}

	public refreshHubspotAndHyperline(clientId: string): Observable<User> {
		return this.http.get<User>(`api/accounts/refreshHubspotAndHyperline/${clientId}`);
	}

	public login(username: string, password: string, rememberMe: boolean = true): Observable<User> {
		return this.http
			.post<AuthResponse>('token', `username=${encodeURIComponent(username)}&password=${encodeURIComponent(password)}&grant_type=password`, {
				headers: new HttpHeaders({
					'Content-Type': 'x-www-form-urlencoded',
				}),
			})
			.pipe(
				switchMap(authResponse => {
					if (authResponse && authResponse.access_token) {
						const userTokenClaims = this.decodeUserToken(authResponse.access_token);
						if (userTokenClaims) {
							const userToken = {
								token: authResponse.access_token,
								claims: userTokenClaims,
							};
							this.setUserToken(userToken, rememberMe);
							return this.getUser();
						} else return empty();
					}
				})
			);
	}

	public logout(): void {
		if (!this.userToken?.switchedFromAdmin) {
			this.purgeUserData();
			return;
		}

		const userId = this.userSubject.value?.id;
		if (userId) {
			sessionStorage.setItem(sessionStorageUserId, userId);
		}

		// Switching back to admin user
		localStorage.removeItem(localStorageControlledUserKey);
		this.userTokenSubject.next(this.getUserToken());
	}

	/**
	 * Lets login as a particular user
	 * Available for Admins only.
	 *
	 * @param username
	 *
	 * @return Observable<User>
	 */
	public forceLogin(username: string): Observable<User> {
		return this.http.post<AuthResponse>('api/accounts/forcelogin', username).pipe(
			switchMap(authResponse => {
				if (authResponse && authResponse.access_token) {
					const userTokenClaims = this.decodeUserToken(authResponse.access_token);
					if (userTokenClaims) {
						const userToken = {
							token: authResponse.access_token,
							claims: userTokenClaims,
							switchedFromAdmin: true,
						};
						this.setUserToken(userToken, true, true);
						return this.getUser();
					} else return empty();
				}
			})
		);
	}

	uploadAvatar(files: File[]): Observable<void> {
		const formData = new FormData();

		for (const file of files) {
			formData.append('file', file, file.name);
		}

		// change the default content type which is json to allow file upload
		return this.http.post<any>('api/users/uploadavatar', formData);
	}

	public updateProtectionPro(userId: string, hasProtectionPro: boolean): Observable<void> {
		return this.http.post<void>(`api/users/updateprotectionpro/${userId}`, { hasProtectionPro: hasProtectionPro });
	}
}
